import { useScopePanel } from '~/composables/web-chat/useScopePanel'
import type { ScopeOpenResult } from '~/composables/web-chat/dispatch'

/**
 * `open_case_or_client` — the agent moving the chat to a case or client the user named (§6b).
 *
 * Two rules shape this:
 *
 * 1. **The name never comes from the model.** The tool takes an id only; the label on the chip and in
 *    the URL is resolved here from the org's own case list — the same stance as `ClearScopeCard`,
 *    whose scope name comes from the store. A model-supplied name would render a scope chip that
 *    disagrees with what is actually being filtered.
 * 2. **Resolve or refuse.** An id the org cannot see (deleted case, another org, a hallucination)
 *    must not be written into `activeScope`: the tools would filter to an empty set and the chat
 *    would look scoped while seeing nothing — the exact failure `knownScopeIds` exists to prevent.
 *
 * Resolution reuses `useScopePanel().ensureScopeLists()` — the session's single, auth-aware fetch of
 * the org's clients and cases, which is also what the scope picker's columns are built from. The
 * client rung used to be resolved by finding a CASE that named the client, so "abre el cliente
 * Palieri" was refused for a client with no cases yet — the agent reporting a client does not exist
 * while it sat in the document browser (AlexAmin/marcus#85). It resolves against the client list now.
 */
export function useOpenScope() {
  const webChat = useWebChatStore()
  const router = useRouter()
  const { clients, cases, ensureScopeLists } = useScopePanel()

  async function openScope(target: { kind: 'case' | 'client'; id: string }): Promise<ScopeOpenResult> {
    if (!target.id) return { ok: false, reason: 'not_found' }
    // A failed load is NOT "no such case" — reporting it as one would have the agent tell the user a
    // case does not exist because a fetch 401'd.
    if (!await ensureScopeLists()) return { ok: false, reason: 'unavailable' }

    if (target.kind === 'client') {
      const found = clients.value.find(c => c.id === target.id)
      if (!found) return { ok: false, reason: 'not_found' }
      await webChat.setScope({ kind: 'client', id: target.id, name: found.name })
      await router.push(`/app/clients/${target.id}`)
      return { ok: true, kind: 'client', name: found.name }
    }

    const found = cases.value.find(c => c.id === target.id)
    if (!found) return { ok: false, reason: 'not_found' }
    await webChat.setScope({
      kind: 'case',
      id: found.id,
      name: found.name,
      clientId: found.client_id ?? null,
      clientName: found.client_name ?? null,
    })
    await router.push(`/app/cases/${found.id}`)
    return { ok: true, kind: 'case', name: found.name }
  }

  return { openScope }
}
